import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AccessPolicyUpdate,
  type AdminFeatureItem,
  clearFeatureAccessPolicy,
  fetchFeatureConfig,
  getAdminFeatures,
  getFeatureAccessPolicy,
  getFeatureBaseAccessPolicy,
  updateFeatureAccessPolicy,
  updateFeatureBaseAccessPolicy,
  updateFeatureConfig,
  updateFeatureStatus,
} from "@/api/admin-api"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

// Feature Management (access policies)
export function useAdminFeaturesQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "features"],
    queryFn: () => getAdminFeatures(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    // Keep previous data to avoid flicker when returning to the tab
    placeholderData: (previousData: AdminFeatureItem[] | undefined) => previousData,
  })
}

export function useUpdateFeatureStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ featureId, enabled }: { featureId: string; enabled: boolean }) => {
      return await updateFeatureStatus(featureId, enabled)
    },
    onMutate: async ({ featureId, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "features"] })

      const previousFeatures = queryClient.getQueryData<AdminFeatureItem[]>(["admin", "features"])
      if (previousFeatures) {
        queryClient.setQueryData(
          ["admin", "features"],
          previousFeatures.map((f) => (f.id === featureId ? { ...f, enabled } : f))
        )
      }

      return { previousFeatures }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousFeatures) {
        queryClient.setQueryData(["admin", "features"], context.previousFeatures)
      }
    },
    onSuccess: () => {
      // Refetch to get latest access_configured/derived flags
      void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
    },
  })
}

// Feature configuration (the per-feature `config` object)
export function useAdminFeatureConfigQuery<TConfig = Record<string, unknown>>(
  featureId: string,
  enabled = true
) {
  return useQuery({
    queryKey: ["admin", "features", featureId, "config"],
    queryFn: async () => (await fetchFeatureConfig<TConfig>(featureId)).config,
    enabled: enabled && !!featureId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateFeatureConfigMutation<
  TConfig extends Record<string, unknown> = Record<string, unknown>,
>() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ featureId, config }: { featureId: string; config: TConfig }) => {
      return await updateFeatureConfig<TConfig>(featureId, config)
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["admin", "features", variables.featureId, "config"], data.config)
      void queryClient.invalidateQueries({
        queryKey: ["admin", "features", variables.featureId, "config"],
      })
    },
  })
}

export function useAdminFeatureBaseAccessPolicyQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "features", "base", "access"],
    queryFn: () => getFeatureBaseAccessPolicy(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateFeatureBaseAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ policy }: { policy: AccessPolicyUpdate }) => {
      return await updateFeatureBaseAccessPolicy(policy)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "features", "base", "access"] })
    },
  })
}

export function useAdminFeatureAccessPolicyQuery(featureId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "features", featureId, "access"],
    queryFn: () => getFeatureAccessPolicy(featureId ?? ""),
    enabled: enabled && !!featureId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateFeatureAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      featureId,
      policy,
    }: {
      featureId: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateFeatureAccessPolicy(featureId, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "features", variables.featureId, "access"],
      })
    },
  })
}

export function useClearFeatureAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ featureId }: { featureId: string }) => {
      return await clearFeatureAccessPolicy(featureId)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "features", variables.featureId, "access"],
      })
    },
  })
}
